
/* === animations.js === */
class TextSplitter {
  static MAX_CHARS = 100;

  static splitWords(element) {
    const html = element.innerHTML.trim();
    if (!html) return;

    element.setAttribute('aria-label', element.textContent.trim());

    let wordIndex = 0;
    element.innerHTML = html.replace(/(<[^>]+>)|(&[^;]+;)|([^<&\s]+)/g, (match, tag, entity, word) => {
      if (tag) return tag;
      if (entity) return `<span class="word"><span class="word-inner" style="--word-index:${wordIndex++}">${entity}</span></span>`;
      return `<span class="word"><span class="word-inner" style="--word-index:${wordIndex++}">${word}</span></span>`;
    });

    element.dataset.textSplit = 'words';
  }

  static splitChars(element) {
    const text = element.textContent.trim();
    if (!text) return;

    if (text.length > this.MAX_CHARS) {
      this.splitWords(element);
      return;
    }

    element.setAttribute('aria-label', text);

    const html = element.innerHTML.trim();
    let charIndex = 0;
    element.innerHTML = html.replace(/(<[^>]+>)|(&[^;]+;)|([^<&])/g, (match, tag, entity, char) => {
      if (tag) return tag;
      if (entity) return `<span class="char" style="--char-index:${charIndex++}">${entity}</span>`;
      if (char === ' ') return ' ';
      return `<span class="char" style="--char-index:${charIndex++}">${char}</span>`;
    });

    element.dataset.textSplit = 'chars';
  }

  static processAll() {
    document.querySelectorAll('[data-animate="text-words"]:not([data-text-split])').forEach(el => {
      this.splitWords(el);
    });

    document.querySelectorAll('[data-animate="text-chars"]:not([data-text-split])').forEach(el => {
      this.splitChars(el);
    });
  }
}

class ModernAnimations {
  constructor() {
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.animatedElements = new Set();
    this.pendingDelays = new Map();
    this.rafPending = false;
    this.resizeTimer = null;

    this.init();
  }

  init() {
    this.watchReducedMotion();

    if (this.prefersReducedMotion()) {
      this.disableAllAnimations();
      return;
    }

    this.start();
  }

  start() {
    const initTasks = () => {
      TextSplitter.processAll();
      this.recalculateVisualStagger();
      this.setupIntersectionObserver();
      this.setupDynamicContentHandling();
      this.setupResizeHandler();
      this.observeExistingElements();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initTasks, { timeout: 100 });
    } else {
      setTimeout(initTasks, 0);
    }
  }

  setupIntersectionObserver() {
    if (this.intersectionObserver) return;

    const options = {
      root: null,
      rootMargin: '0px 0px -15% 0px',
      threshold: 0
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
          this.animateElement(entry.target);
        }
      });
    }, options);
  }

  observeExistingElements() {
    if (!this.intersectionObserver) return;

    const elements = document.querySelectorAll('[data-animate]:not(.animate):not([data-animate-manual])');
    elements.forEach(el => {
      if (el.closest('.slide-clone')) return;
      if (el.offsetParent === null) return;
      this.intersectionObserver.observe(el);
    });
  }

  unobserveElement(element) {
    if (this.intersectionObserver && element) {
      this.intersectionObserver.unobserve(element);
    }
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  watchReducedMotion() {
    if (this._motionMq) return;

    this._motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._motionHandler = (e) => {
      if (e.matches) {
        this.disableAllAnimations();
        this.destroy();
      } else {
        this.enableAllAnimations();
        this.start();
      }
    };
    this._motionMq.addEventListener('change', this._motionHandler);
  }

  animateElement(element) {
    if (this.animatedElements.has(element)) return;

    this.animatedElements.add(element);
    this.unobserveElement(element);

    const delay = element.getAttribute('data-animate-delay') || '0';
    const delayMs = parseInt(delay, 10);

    if (delayMs > 0) {
      const timerId = setTimeout(() => {
        this.pendingDelays.delete(element);
        this.triggerAnimation(element);
      }, delayMs);
      this.pendingDelays.set(element, timerId);
    } else {
      this.triggerAnimation(element);
    }
  }

  triggerAnimation(element) {
    requestAnimationFrame(() => {
      element.classList.add('animate');
      this.cleanupAfterAnimation(element);
    });
  }

  cleanupAfterAnimation(element) {
    const duration = this.getAnimationDuration(element);
    setTimeout(() => {
      if (!element.hasAttribute('data-parallax')) {
        element.style.willChange = 'auto';
      }
    }, duration + 100);
  }

  getAnimationDuration(element) {
    const animType = element.getAttribute('data-animate');
    const durationAttr = element.getAttribute('data-animate-duration');

    const durations = {
      'reveal':     [500, 900, 1300],
      'elastic':    [400, 700, 1200],
      'stagger-3d': [400, 800, 1200],
      'flip':       [400, 600, 1200],
    };

    const [fast, normal, slow] = durations[animType] || [300, 600, 900];

    switch (durationAttr) {
      case 'fast': return fast;
      case 'slow': return slow;
      default: return normal;
    }
  }

  recalculateVisualStagger() {
    const processed = new Set();
    const allSectionData = [];

    const staggerElements = document.querySelectorAll('[data-stagger-index]:not(.animate)');
    const elementsBySection = new Map();

    staggerElements.forEach(el => {
      const section = el.closest('.shopify-section');
      if (!section) return;
      if (!elementsBySection.has(section)) {
        elementsBySection.set(section, []);
      }
      elementsBySection.get(section).push(el);
    });

    elementsBySection.forEach((elements, section) => {
      if (elements.length < 2) return;

      const rects = elements.map(el => ({
        el,
        top: el.getBoundingClientRect().top,
        step: parseInt(el.getAttribute('data-stagger-step') || '100', 10)
      }));
      rects.sort((a, b) => a.top - b.top);
      allSectionData.push(rects);
    });

    allSectionData.forEach(rects => {
      rects.forEach(({ el, step }, i) => {
        el.setAttribute('data-stagger-index', i);
        const delay = i * step;
        el.setAttribute('data-animate-delay', delay > 0 ? delay.toString() : '0');
      });
    });
  }

  setupResizeHandler() {
    if (this._resizeHandler) return;
    this._resizeHandler = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.recalculateVisualStagger();
      }, 250);
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  setupDynamicContentHandling() {
    if (!this._sectionLoadHandler) {
      this._sectionLoadHandler = () => {
        setTimeout(() => {
          TextSplitter.processAll();
          this.recalculateVisualStagger();
          this.observeExistingElements();
        }, 100);
      };

      this._sectionUnloadHandler = (e) => {
        const section = e.target;
        if (!section) return;
        section.querySelectorAll('[data-animate]').forEach(el => {
          if (this.pendingDelays.has(el)) {
            clearTimeout(this.pendingDelays.get(el));
            this.pendingDelays.delete(el);
          }
          this.unobserveElement(el);
          this.animatedElements.delete(el);
        });
      };

      document.addEventListener('shopify:section:load', this._sectionLoadHandler);
      document.addEventListener('shopify:section:unload', this._sectionUnloadHandler);
    }

    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver((mutations) => {
        if (this.rafPending) return;

        let hasNew = false;
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) {
              if (node.hasAttribute?.('data-animate') || node.querySelector?.('[data-animate]')) {
                hasNew = true;
                break;
              }
            }
          }
          if (hasNew) break;
        }

        if (!hasNew) return;

        this.rafPending = true;
        requestAnimationFrame(() => {
          TextSplitter.processAll();
          requestAnimationFrame(() => {
            this.rafPending = false;
            this.recalculateVisualStagger();
            this.observeExistingElements();
          });
        });
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  disableAllAnimations() {
    document.documentElement.classList.add('animations-disabled');
  }

  enableAllAnimations() {
    document.documentElement.classList.remove('animations-disabled');
  }

  refresh() {
    TextSplitter.processAll();
    this.recalculateVisualStagger();
    setTimeout(() => this.observeExistingElements(), 100);
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.pendingDelays.forEach(timerId => clearTimeout(timerId));
    this.pendingDelays.clear();
    this.animatedElements.clear();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
      clearTimeout(this.resizeTimer);
    }
    if (this._motionHandler) {
      this._motionMq?.removeEventListener('change', this._motionHandler);
      this._motionHandler = null;
    }
  }

  animate(selector) {
    const elements = typeof selector === 'string'
      ? document.querySelectorAll(selector)
      : [selector];

    elements.forEach(element => {
      if (element && element.hasAttribute('data-animate')) {
        this.animateElement(element);
      }
    });
  }
}

if (!window.ModernAnimations) {
  window.ModernAnimations = new ModernAnimations();

  window.animateElement = (selector) => {
    window.ModernAnimations.animate(selector);
  };

  window.refreshAnimations = () => {
    window.ModernAnimations.refresh();
  };
}


/* === button-effects.js === */
(function () {
  const body = document.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return;

  if (body.dataset.buttonsHover === 'paint') {
    const setMousePos = (e) => {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      btn.style.setProperty('--mouse-x', (e.clientX - rect.left) + 'px');
      btn.style.setProperty('--mouse-y', (e.clientY - rect.top) + 'px');
    };

    const bindPaint = (root) => {
      root.querySelectorAll('.button').forEach((btn) => {
        if (btn.dataset.paintBound) return;
        btn.dataset.paintBound = '1';
        btn.addEventListener('mouseenter', setMousePos);
        btn.addEventListener('mouseleave', setMousePos);
      });
    };

    bindPaint(document);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length) bindPaint(document);
      }
    }).observe(body, { childList: true, subtree: true });
  }

  if (body.dataset.buttonsHover === 'magnetic') {
    const MAX_OFFSET = 6;

    const handleMouseMove = (e) => {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = (e.clientX - centerX) / (rect.width / 2);
      const dy = (e.clientY - centerY) / (rect.height / 2);
      const tx = dx * MAX_OFFSET;
      const ty = dy * MAX_OFFSET;
      btn.style.transform = 'translate(' + tx + 'px, ' + ty + 'px)';
    };

    const handleMouseLeave = (e) => {
      e.currentTarget.style.transform = '';
    };

    const bindMagnetic = (root) => {
      root.querySelectorAll('.button').forEach((btn) => {
        if (btn.dataset.magneticBound) return;
        btn.dataset.magneticBound = '1';
        btn.addEventListener('mousemove', handleMouseMove);
        btn.addEventListener('mouseleave', handleMouseLeave);
      });
    };

    bindMagnetic(document);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length) bindMagnetic(document);
      }
    }).observe(body, { childList: true, subtree: true });
  }
})();


/* === hide-cookie-banner.js === */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  const hideCookieBanner = () => {
    document.querySelectorAll('[id^="shopify-pc__"], .shopify-pc__banner__dialog').forEach(el => {
      el.style.display = 'none';
    });
  };

  hideCookieBanner();
  document.addEventListener('shopify:section:reloaded', hideCookieBanner);

  new MutationObserver(hideCookieBanner).observe(document.body, {
    childList: true,
    subtree: true
  });
}


/* === search-form.js === */
class SearchForm extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input[type="search"]');
        this.resetButton = this.querySelector('button[type="reset"]');

        if (this.input) {
            this.input.form.addEventListener('reset', this.onFormReset.bind(this));
            this.input.addEventListener(
                'input',
                debounce(event => {
                    this.onChange(event);
                }, 300).bind(this)
            );
        }
    }

    toggleResetButton() {
        const resetIsHidden = this.resetButton.classList.contains('hidden');
        if (this.input.value.length > 0 && resetIsHidden) {
            this.resetButton.classList.remove('hidden');
        } else if (this.input.value.length === 0 && !resetIsHidden) {
            this.resetButton.classList.add('hidden');
        }
    }

    onChange() {
        this.toggleResetButton();
    }

    shouldResetForm() {
        return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
        event.preventDefault();
        if (this.shouldResetForm()) {
            this.input.value = '';
            this.input.focus();
            this.toggleResetButton();
        }
    }
}

customElements.define('search-form', SearchForm);


/* === header-menu.js === */
if (!customElements.get('header-menu-controller')) {
  customElements.define('header-menu-controller', class HeaderMenuController extends HTMLElement {
    constructor() {
      super();
      this.activeItem = null;
      this.hoverTimeouts = new Map();
      this.isTouch = 'ontouchstart' in window;
      this.isMobile = window.innerWidth < 750;
    }

    connectedCallback() {
      this.menuType = this.dataset.menuType || 'dropdown';
      this.triggerMode = this.dataset.menuTrigger || 'hover';
      this.animation = this.dataset.menuAnimation || 'fade_slide';

      this.menuItems = this.querySelectorAll('[data-menu-item]');
      this.tabContainers = this.querySelectorAll('[data-mega-tabs]');
      this.accordions = this.querySelectorAll('[data-accordion]');
      this.drilldownMenus = this.querySelectorAll('[data-drilldown-menu]');

      this.initDesktopMenu();
      this.initMobileMenu();
      this.initTabs();
      this.initKeyboard();
      this.initResizeHandler();
      this.initGlobalHandlers();
    }

    disconnectedCallback() {
      this.hoverTimeouts.forEach(timeout => clearTimeout(timeout));
      this.hoverTimeouts.clear();
      this.closeAllMenus();

      if (this.boundOutsideClick) {
        document.removeEventListener('click', this.boundOutsideClick);
      }
      if (this.boundEscapeKey) {
        document.removeEventListener('keydown', this.boundEscapeKey);
      }
      if (this.boundCloseMenu) {
        document.removeEventListener('header:close-menu', this.boundCloseMenu);
      }
      if (this.boundResize) {
        window.removeEventListener('resize', this.boundResize);
      }
    }

    initDesktopMenu() {
      if (!this.menuItems.length) return;

      this.menuItems.forEach(item => {
        const trigger = item.querySelector('[data-menu-trigger]');
        if (!trigger) return;

        if (this.triggerMode === 'hover' && !this.isTouch) {
          this.initHoverBehavior(item, trigger);
        }

        this.initClickBehavior(item, trigger);
        this.initSubmenuPanels(item);
      });
    }

    initSubmenuPanels(item) {
      const submenuTriggers = item.querySelectorAll('[data-submenu-trigger]');
      if (!submenuTriggers.length) return;

      const dropdown = item.querySelector('[data-menu-dropdown]');
      if (!dropdown) return;

      submenuTriggers.forEach(trigger => {
        const panelId = trigger.dataset.submenuTrigger;

        trigger.addEventListener('mouseenter', () => {
          dropdown.querySelectorAll('[data-submenu-panel]').forEach(p => {
            p.setAttribute('aria-hidden', 'true');
          });
          const panel = dropdown.querySelector(`[data-submenu-panel="${panelId}"]`);
          if (panel) panel.setAttribute('aria-hidden', 'false');
          submenuTriggers.forEach(t => t.classList.remove('is-active'));
          trigger.classList.add('is-active');
        });
      });

      item.addEventListener('menu:closed', () => {
        dropdown.querySelectorAll('[data-submenu-panel]').forEach(p => {
          p.setAttribute('aria-hidden', 'true');
        });
        submenuTriggers.forEach(t => t.classList.remove('is-active'));
      });
    }

    initHoverBehavior(item, trigger) {
      const openDelay = 80;
      const closeDelay = 250;

      trigger.addEventListener('mouseenter', () => {
        if (this.isMobile) return;

        this.clearHoverTimeout(item);
        const timeout = setTimeout(() => {
          this.openMenu(item);
        }, openDelay);
        this.hoverTimeouts.set(item, timeout);
      });

      trigger.addEventListener('mouseleave', () => {
        if (this.isMobile) return;

        this.clearHoverTimeout(item);
        const timeout = setTimeout(() => {
          this.closeMenu(item);
        }, closeDelay);
        this.hoverTimeouts.set(item, timeout);
      });

      const dropdown = item.querySelector('[data-menu-dropdown]');
      if (dropdown) {
        dropdown.addEventListener('mouseenter', () => {
          if (this.isMobile) return;
          this.clearHoverTimeout(item);
        });

        dropdown.addEventListener('mouseleave', () => {
          if (this.isMobile) return;

          this.clearHoverTimeout(item);
          const timeout = setTimeout(() => {
            this.closeMenu(item);
          }, closeDelay);
          this.hoverTimeouts.set(item, timeout);
        });
      }
    }

    initClickBehavior(item, trigger) {
      trigger.addEventListener('click', (e) => {
        if (this.isTouch || this.triggerMode === 'click') {
          e.preventDefault();
          this.toggleMenu(item);
        } else if (this.triggerMode === 'hover' && item.classList.contains('header__menu-item--active')) {
          e.preventDefault();
          this.closeMenu(item);
        }
      });
    }

    initGlobalHandlers() {
      this.boundOutsideClick = (e) => {
        if (!this.activeItem) return;
        if (!this.activeItem.contains(e.target)) {
          this.closeMenu(this.activeItem);
        }
      };
      document.addEventListener('click', this.boundOutsideClick);

      this.boundEscapeKey = (e) => {
        if (e.key === 'Escape' && this.activeItem) {
          this.closeMenu(this.activeItem);
          const trigger = this.activeItem.querySelector('[data-menu-trigger]');
          if (trigger) trigger.focus();
        }
      };
      document.addEventListener('keydown', this.boundEscapeKey);

      this.boundCloseMenu = () => {
        if (this.activeItem) {
          this.closeMenu(this.activeItem);
        }
      };
      document.addEventListener('header:close-menu', this.boundCloseMenu);
    }

    initMobileMenu() {
      this.initAccordions();
      this.initDrilldown();
    }

    initAccordions() {
      if (!this.accordions.length) return;

      this.accordions.forEach(accordion => {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const panel = accordion.querySelector('[data-accordion-panel]');

        if (!trigger || !panel) return;

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleAccordion(accordion, trigger, panel);
        });
      });
    }

    toggleAccordion(accordion, trigger, panel) {
      const isOpen = accordion.classList.contains('is-open');

      const parent = accordion.parentElement;
      if (parent) {
        parent.querySelectorAll('[data-accordion].is-open').forEach(sibling => {
          if (sibling !== accordion) {
            sibling.classList.remove('is-open');
            const siblingPanel = sibling.querySelector('[data-accordion-panel]');
            const siblingTrigger = sibling.querySelector('[data-accordion-trigger]');
            if (siblingPanel) siblingPanel.style.gridTemplateRows = '0fr';
            if (siblingTrigger) siblingTrigger.setAttribute('aria-expanded', 'false');
          }
        });
      }

      if (isOpen) {
        accordion.classList.remove('is-open');
        panel.style.gridTemplateRows = '0fr';
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        accordion.classList.add('is-open');
        panel.style.gridTemplateRows = '1fr';
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    initDrilldown() {
      if (!this.drilldownMenus.length) return;

      this.drilldownMenus.forEach(menu => {
        const triggers = menu.querySelectorAll('[data-drilldown-trigger]');
        triggers.forEach(trigger => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = trigger.dataset.drilldownTrigger;
            this.navigateDrilldown(menu, targetId, 'forward');
          });
        });

        const backButtons = menu.querySelectorAll('[data-drilldown-back]');
        backButtons.forEach(back => {
          back.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = back.dataset.drilldownBack;
            this.navigateDrilldown(menu, targetId, 'back');
          });
        });
      });
    }

    navigateDrilldown(menu, targetId, direction) {
      const currentPage = menu.querySelector('[data-drilldown-page].is-active');
      const targetPage = menu.querySelector(`[data-drilldown-page="${targetId}"]`);

      if (!targetPage) return;

      menu.classList.add(`drilldown--${direction}`);

      if (currentPage) {
        currentPage.classList.remove('is-active');
        currentPage.setAttribute('aria-hidden', 'true');
      }

      targetPage.classList.add('is-active');
      targetPage.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        menu.classList.remove(`drilldown--${direction}`);
      }, 300);

      const firstFocusable = targetPage.querySelector('a, button');
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }

    initTabs() {
      if (!this.tabContainers.length) return;

      this.tabContainers.forEach(container => {
        const tabButtons = container.querySelectorAll('[data-tab-button]');
        const tabPanels = container.querySelectorAll('[data-tab-panel]');

        if (!tabButtons.length || !tabPanels.length) return;

        tabButtons.forEach((button, index) => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.switchTab(container, button, tabButtons, tabPanels);
          });

          button.addEventListener('keydown', (e) => {
            this.handleTabKeyboard(e, tabButtons, index);
          });
        });
      });
    }

    switchTab(container, activeButton, allButtons, allPanels) {
      const targetId = activeButton.dataset.tabButton;

      allButtons.forEach(button => {
        const isActive = button === activeButton;
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.classList.toggle('is-active', isActive);
      });

      allPanels.forEach(panel => {
        const isActive = panel.dataset.tabPanel === targetId;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.classList.toggle('is-active', isActive);
      });
    }

    handleTabKeyboard(e, tabs, currentIndex) {
      let newIndex;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
      } else if (e.key === 'Home') {
        e.preventDefault();
        newIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        newIndex = tabs.length - 1;
      }

      if (newIndex !== undefined) {
        tabs[newIndex].focus();
        tabs[newIndex].click();
      }
    }

    initKeyboard() {
      this.menuItems.forEach(item => {
        const dropdown = item.querySelector('[data-menu-dropdown]');
        if (!dropdown) return;

        item.addEventListener('transitionend', (e) => {
          if (e.target === dropdown && item.classList.contains('header__menu-item--active')) {
            const firstLink = dropdown.querySelector('a');
            if (firstLink && document.activeElement.closest('[data-menu-trigger]')) {
              if (this.triggerMode === 'click') {
                setTimeout(() => firstLink.focus(), 50);
              }
            }
          }
        });
      });
    }

    initResizeHandler() {
      let resizeTimeout;
      this.boundResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const wasMobile = this.isMobile;
          this.isMobile = window.innerWidth < 750;

          if (wasMobile !== this.isMobile) {
            this.closeAllMenus();
          }
        }, 150);
      };
      window.addEventListener('resize', this.boundResize);
    }

    toggleMenu(item) {
      if (item.classList.contains('header__menu-item--active')) {
        this.closeMenu(item);
      } else {
        this.openMenu(item);
      }
    }

    openMenu(item) {
      if (this.activeItem && this.activeItem !== item) {
        this.closeMenu(this.activeItem);
      }

      if (this.animation) {
        item.classList.add(`menu-animation--${this.animation}`);
      }

      item.classList.add('header__menu-item--active');
      this.activeItem = item;

      const trigger = item.querySelector('[data-menu-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      item.dispatchEvent(new CustomEvent('menu:opened', { bubbles: true }));
    }

    closeMenu(item) {
      item.classList.remove('header__menu-item--active');

      if (this.activeItem === item) {
        this.activeItem = null;
      }

      const trigger = item.querySelector('[data-menu-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }

      this.clearHoverTimeout(item);

      item.dispatchEvent(new CustomEvent('menu:closed', { bubbles: true }));
    }

    closeAllMenus() {
      this.menuItems.forEach(item => {
        if (item.classList.contains('header__menu-item--active')) {
          this.closeMenu(item);
        }
      });
    }

    clearHoverTimeout(item) {
      if (this.hoverTimeouts.has(item)) {
        clearTimeout(this.hoverTimeouts.get(item));
        this.hoverTimeouts.delete(item);
      }
    }
  });
}


/* === header.js === */
class HeaderController {
  constructor() {
    this.header = document.querySelector('[data-header-wrapper]');
    if (!this.header) return;

    this.section = this.header.closest('.section-header');
    this.settings = this.getSettings();

    this.lastScrollY = 0;
    this.currentScrollY = 0;
    this.ticking = false;
    this.isHidden = false;
    this.scrollThreshold = 5;

    if (this.settings.sticky) {
      this.initStickyBehavior();
    }

    if (this.settings.transparent && this.settings.sticky) {
      this.initTransparentHeader();
    }

    this.initMobileDrawer();

    this.initMobileMenu();
  }

  cleanup() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    if (this.transparentScrollHandler) {
      window.removeEventListener('scroll', this.transparentScrollHandler);
      this.transparentScrollHandler = null;
    }

    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
    }

    if (this.section) {
      this.section.classList.remove('section-header--hidden', 'section-header--scrolled', 'scrolled');
    }
  }

  getSettings() {
    return {
      sticky: this.section && this.section.classList.contains('section-header--sticky'),
      transparent: this.header.classList.contains('header-wrapper--transparent'),
      stickyBehavior: this.header.dataset.stickyBehavior || 'always'
    };
  }

  initStickyBehavior() {
    if (this.settings.stickyBehavior === 'always') {
      return;
    }

    if (this.settings.stickyBehavior === 'on_scroll_up') {
      this.initHideOnScrollDown();
    }
  }

  initHideOnScrollDown() {
    if (!this.section) return;

    this.headerHeight = this.header.offsetHeight;

    this.stickyAnnouncementSection = null;
    this.announcementOriginalTop = null;

    const announcementBar = document.querySelector('.section-announcement');
    if (announcementBar) {
      const announcementSection = announcementBar.closest('[id^="shopify-section-"]');
      if (announcementSection) {
        const inlineTop = announcementSection.style.top;
        if (inlineTop || announcementSection.style.position === 'sticky') {
          this.stickyAnnouncementSection = announcementSection;
          this.announcementOriginalTop = parseInt(inlineTop) || this.headerHeight;
          announcementSection.style.transition = 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        }
      }
    }

    const handleScroll = () => {
      this.currentScrollY = window.scrollY;
      const scrollDiff = this.currentScrollY - this.lastScrollY;
      const absScrollDiff = Math.abs(scrollDiff);

      if (absScrollDiff < this.scrollThreshold) return;

      if (this.currentScrollY <= this.headerHeight) {
        if (this.isHidden) {
          this.section.classList.remove('section-header--hidden');
          this.updateAnnouncementPosition(false);
          this.isHidden = false;
        }
        this.section.classList.toggle('section-header--scrolled', this.currentScrollY > 0);
      } else if (scrollDiff > 0 && this.currentScrollY > this.headerHeight * 2) {
        if (!this.isHidden) {
          this.section.classList.add('section-header--hidden');
          this.section.classList.add('section-header--scrolled');
          this.updateAnnouncementPosition(true);
          this.isHidden = true;
        }
      } else if (scrollDiff < 0) {
        if (this.isHidden) {
          this.section.classList.remove('section-header--hidden');
          this.updateAnnouncementPosition(false);
          this.isHidden = false;
        }
        this.section.classList.add('section-header--scrolled');
      }

      this.lastScrollY = this.currentScrollY;
    };

    this.scrollHandler = () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    window.addEventListener('scroll', this.scrollHandler);
  }

  
  updateAnnouncementPosition(headerHidden) {
    if (!this.stickyAnnouncementSection) return;

    if (headerHidden) {
      this.stickyAnnouncementSection.style.top = '0px';
    } else {
      this.stickyAnnouncementSection.style.top = `${this.announcementOriginalTop}px`;
    }
  }

  initTransparentHeader() {
    if (!this.section) return;

    let threshold = this.headerHeight || this.header.offsetHeight;
    const announcementBar = document.querySelector('.section-announcement');
    if (announcementBar) {
      threshold += announcementBar.offsetHeight;
    }

    const handleTransparent = () => {
      if (window.scrollY > threshold) {
        this.section.classList.add('scrolled');
      } else {
        this.section.classList.remove('scrolled');
      }
    };

    handleTransparent();

    this.transparentScrollHandler = this.throttle(handleTransparent, 100);

    window.addEventListener('scroll', this.transparentScrollHandler);
  }

  
  initMobileDrawer() {
    const toggleButton = document.querySelector('[data-drawer-toggle]');
    const drawer = document.querySelector('[data-drawer]');
    const overlay = document.querySelector('[data-drawer-overlay]');
    const closeButton = document.querySelector('[data-drawer-close]');

    if (!toggleButton || !drawer) return;

    toggleButton.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = drawer.classList.contains('drawer--open');

      if (isOpen) {
        this.closeDrawer(drawer, toggleButton);
      } else {
        this.openDrawer(drawer, toggleButton);
      }
    });

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeDrawer(drawer, toggleButton);
      });
    }

    if (overlay) {
      overlay.addEventListener('click', () => {
        this.closeDrawer(drawer, toggleButton);
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (drawer && drawer.classList.contains('drawer--open')) {
          this.closeDrawer(drawer, toggleButton);
        }

        const searchModal = document.querySelector('[data-search-modal]');
        if (searchModal && searchModal.classList.contains('search-modal--active')) {
          document.dispatchEvent(new CustomEvent('header:close-search'));
        }

        const activeMenu = document.querySelector('.header__menu-item--active');
        if (activeMenu) {
          document.dispatchEvent(new CustomEvent('header:close-menu'));
        }
      }
    });
  }

  openDrawer(drawer, toggleButton) {
    drawer.classList.add('drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const focusableElements = drawer.querySelectorAll(
      'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length) {
      focusableElements[0].focus();
    }
  }

  closeDrawer(drawer, toggleButton) {
    drawer.classList.remove('drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (toggleButton) {
      toggleButton.focus();
    }
  }

  initMobileMenu() {
    const drawer = document.querySelector('[data-drawer]');
    if (!drawer) return;

    const menuType = drawer.dataset.drawerMenuType || 'accordion';

    if (menuType === 'accordion') {
      this.initAccordionMenu(drawer);
    } else if (menuType === 'drilldown') {
      this.initDrilldownMenu(drawer);
    }
  }

  initAccordionMenu(container) {
    const triggers = container.querySelectorAll('[data-accordion-trigger]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const accordion = trigger.closest('[data-accordion]');
        const panel = accordion.querySelector('[data-accordion-panel]');

        if (!panel) return;

        const isOpen = accordion.hasAttribute('data-accordion-open');

        if (isOpen) {
          accordion.removeAttribute('data-accordion-open');
          panel.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          accordion.setAttribute('data-accordion-open', '');
          panel.setAttribute('aria-hidden', 'false');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  initDrilldownMenu(container) {
    const triggers = container.querySelectorAll('[data-drilldown-trigger]');
    const backButtons = container.querySelectorAll('[data-drilldown-back]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPage = trigger.dataset.drilldownTrigger;
        this.navigateDrilldown(container, targetPage);
      });
    });

    backButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPage = button.dataset.drilldownBack;
        this.navigateDrilldown(container, targetPage, true);
      });
    });
  }

  navigateDrilldown(container, targetPageId, isBack = false) {
    const pages = container.querySelectorAll('[data-drilldown-page]');
    const targetPage = container.querySelector(`[data-drilldown-page="${targetPageId}"]`);

    if (!targetPage) return;

    pages.forEach(page => {
      const isTarget = page.dataset.drilldownPage === targetPageId;
      const isActive = page.classList.contains('is-active');

      if (isTarget) {
        page.classList.add('is-active');
        page.setAttribute('aria-hidden', 'false');
      } else if (isActive && !isBack) {
        page.classList.remove('is-active');
        page.classList.add('is-previous');
        page.setAttribute('aria-hidden', 'true');

        setTimeout(() => page.classList.remove('is-previous'), 300);
      } else {
        page.classList.remove('is-active', 'is-previous');
        page.setAttribute('aria-hidden', 'true');
      }
    });
  }

  throttle(func, wait) {
    let timeout;
    let previous = 0;

    return (...args) => {
      const now = Date.now();
      const remaining = wait - (now - previous);

      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        previous = now;
        func(...args);
      } else if (!timeout) {
        timeout = setTimeout(() => {
          previous = Date.now();
          timeout = null;
          func(...args);
        }, remaining);
      }
    };
  }
}

